/**
 * 申請一覧 `/hr/requests/list?kind=`。入社・退社・異動・休職・復職・変更の申請を 1 つの一覧で見る
 * (旧 5 画面の一覧部分を畳んだ)。行を押すと右パネル(概要 + 承認ルート)、「新しい申請」でウィザードへ。
 */
import { useQueries } from "@tanstack/react-query";
import { Alert, Button, Space, Tag } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { WfRequest } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  DataTable,
  FilterBar,
  PageHeader,
  PARTIAL_CLEAR_LABEL,
  PartialResultNote,
  partialResultText,
  TimeAgo,
  multiSelectCondition,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type DataColumn,
} from "../../ui";
import { RequestPanel } from "./RequestPanel";
import { REQUEST_STATUSES, WF_KINDS, kindLabel } from "./constants";
import { errorText } from "./hooks";
import { SlaTag } from "./wfActions";
import { employeeLink, matchesAll, requestStatusTag } from "./wfBits";
import { normalizeKind } from "./requestKinds";
import { isOverdue, useRequests, useWfPerms } from "./wfHooks";

const LOCAL_LIMIT = "200";
const VIEWS = [{ value: "all", label: "すべて" }, ...WF_KINDS];

function useKindCounts() {
  const api = useApi();
  const results = useQueries({
    queries: VIEWS.map((v) => ({
      queryKey: ["hr", "wf-requests", "count", "kind", v.value],
      queryFn: () =>
        api.wfRequests(new URLSearchParams({ limit: "1", offset: "0", ...(v.value === "all" ? {} : { kind: v.value }) })),
      select: (d: { total: number }) => d.total,
      staleTime: 60_000,
    })),
  });
  return Object.fromEntries(VIEWS.map((v, i) => [v.value, results[i].data]));
}

export function RequestListPage() {
  const { canOperate } = useWfPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const kind = normalizeKind(sp.get("kind")) ?? "all";
  const statuses = (sp.get("status") ?? "").split(",").filter(Boolean);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const local = Boolean(q) || statuses.length > 1;
  const filters: Record<string, string> = {
    ...(kind === "all" ? {} : { kind }),
    ...(statuses.length === 1 ? { status: statuses[0] } : {}),
  };
  const query = useRequests(
    local
      ? { ...filters, limit: LOCAL_LIMIT, offset: "0" }
      : { ...filters, limit: String(pageSize), offset: String((page - 1) * pageSize) },
  );
  const counts = useKindCounts();
  const filterKey = `${kind}|${q}|${statuses}`;
  useEffect(() => setPage(1), [filterKey]);

  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    if (!local) return items;
    return items.filter(
      (r) =>
        (statuses.length < 2 || statuses.includes(r.status)) &&
        matchesAll(q, r.id, r.employee_name, r.kind_label, r.template_name, r.requested_by, r.start_date),
    );
  }, [query.data, local, statuses, q]);
  const total = local ? rows.length : (query.data?.total ?? 0);
  // 手元で絞っていて、取得が上限で切れている(これより古い申請は対象外。fix1 中4)
  const fetched = query.data?.items.length ?? 0;
  const partial = local && (query.data?.total ?? 0) > Number(LOCAL_LIMIT);

  const columns: DataColumn<WfRequest>[] = [
    { title: "#", ellipsis: false, dataIndex: "id", width: 110, render: (v: number) => <span className="ui-num">{v}</span> },
    {
      title: "申請",
      ellipsis: false,
      width: 220,
      render: (_: unknown, r) => (
        <div>
          <Link to={`/hr/wf-requests/${r.id}`}>{kindLabel(r.kind)}</Link>
          {r.template_name && <span className="ui-cell-sub">{r.template_name}</span>}
        </div>
      ),
    },
    { title: "対象者", ellipsis: false, width: 170, render: (_: unknown, r) => employeeLink(r.employee_id, r.employee_name, r.employee_status) },
    { title: "日付", ellipsis: false, dataIndex: "start_date", width: 120 },
    {
      title: "状態",
      ellipsis: false,
      width: 170,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {requestStatusTag(r)}
          <SlaTag sla={r.sla} />
        </Space>
      ),
    },
    {
      title: "承認",
      ellipsis: false,
      width: 170,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          <span className="ui-num">{r.approvals}</span>
          {r.approved_by.map((a) => (
            <Tag key={a}>{a}</Tag>
          ))}
        </Space>
      ),
    },
    { title: "タスク", ellipsis: false, width: 160, render: (_: unknown, r) => (r.task_total ? <span className="ui-num">{`${r.task_done} / ${r.task_total}`}</span> : "") },
    {
      title: "申請者",
      ellipsis: false,
      width: 170,
      render: (_: unknown, r) => (
        <div>
          <div>{r.requested_by}</div>
          <div className="ui-cell-sub">
            <TimeAgo iso={r.created_at} />
          </div>
        </div>
      ),
    },
  ];

  const conditions = [
    multiSelectCondition({
      key: "status",
      label: "状態",
      options: REQUEST_STATUSES,
      selected: statuses,
      onChange: (vals) => patch({ status: vals.length ? vals.join(",") : null }, true),
    }),
  ];
  const filtered = Boolean(q) || statuses.length > 0;
  const clearAll = () => {
    setQInput("");
    patch({ q: null, status: null }, true);
  };
  const newHref = `/hr/requests/new${kind === "all" ? "" : `?kind=${kind}`}`;
  const panelReq = panelId ? rows.find((r) => r.id === Number(panelId)) : undefined;

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="申請一覧"
          description="入社・退社・異動・休職・復職・変更の申請。承認が揃うとテンプレートの工程がタスクになる。"
          primary={
            canOperate ? (
              <Link to={newHref}>
                <Button type="primary">新しい申請</Button>
              </Link>
            ) : undefined
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "対象者 / テンプレート / 申請者" }}
          views={{
            items: VIEWS.map((v) => ({ key: v.value, label: v.label, count: counts[v.value] })),
            active: kind,
            onChange: (key) => patch({ kind: key === "all" ? null : key }, true),
          }}
          conditions={conditions}
          right={
            filtered ? (
              <Button size="small" onClick={clearAll}>
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="申請を取得できなかった" description={errorText(query.error)} />
        )}
        {partial && <PartialResultNote limit={Number(LOCAL_LIMIT)} onClear={clearAll} />}
        <DataTable<WfRequest>
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={total}
          countLabel={partial ? `${fetched} 件中 ${rows.length} 件` : undefined}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          selectedKey={panelId ? Number(panelId) : null}
          onRowClick={(row) => setPanelId(String(row.id))}
          rowTone={(row) => (isOverdue(row) ? "danger" : undefined)}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: `${filtered ? "条件に合う申請はない" : kind === "all" ? "申請はまだない" : `${kindLabel(kind)}申請はまだない`}${
              partial ? `(${partialResultText(Number(LOCAL_LIMIT))})` : ""
            }`,
            action: partial ? (
              <Button onClick={clearAll}>{PARTIAL_CLEAR_LABEL}</Button>
            ) : filtered ? (
              <Button onClick={clearAll}>条件を外す</Button>
            ) : canOperate ? (
              <Link to={newHref}>
                <Button type="primary">新しい申請</Button>
              </Link>
            ) : undefined,
          }}
        />
      </div>
      {panelId && <RequestPanel id={Number(panelId)} req={panelReq} onClose={() => setPanelId(null)} />}
    </div>
  );
}
